import dayjs from 'dayjs';

import { PriceType } from '@cast/shared-types';
import { getPriceMultiplier } from '@cast/utils';

import {
  ComputeSpendChartData,
  CostReportChartDataByPeriod,
  CostReportChartType,
} from 'features/cost-report/types/costOverTime';
import { getUptimeMultiplier } from 'features/cost-report/utils/costOverTime';
import { findNearestDataPointToTimestamp } from 'utils/data/findNearestDataPointToTimestamp';

import { makeCumulativeReducerFn } from '../../utils';

type ProcessCumulativeCostChartDataParams = {
  current: {
    chartData: CostReportChartDataByPeriod[];
    isLoading: boolean;
  };
  projectEndOfDayCost: boolean;
  applyUptime: boolean;
  storageCostGranularity: PriceType;
  anomalies: Array<{ timestamp?: string }>;
  chartType: CostReportChartType;
};

type ProcessCumulativeCostChartDataResult = {
  isLoading: boolean;
  data: ComputeSpendChartData[];
};

const reduceSelectedRateCost = (chartType: CostReportChartType) => {
  const multiplier =
    chartType === CostReportChartType.BAR
      ? getPriceMultiplier(PriceType.DAILY)
      : getPriceMultiplier(PriceType.HOURLY);

  return makeCumulativeReducerFn({
    onDemand: multiplier,
    fallback: multiplier,
    spot: multiplier,
    storage: multiplier,
  });
};

type ReduceUptimeCostArgs = {
  data: ComputeSpendChartData[];
  datapoint: CostReportChartDataByPeriod;
  index: number;
  applyUptime: boolean;
  storageCostGranularity: PriceType;
};

const reduceUptimeCost = ({
  data,
  datapoint,
  index,
  applyUptime,
  storageCostGranularity,
}: ReduceUptimeCostArgs): ComputeSpendChartData[] => {
  const multiplier = applyUptime
    ? getUptimeMultiplier(datapoint)
    : { onDemand: 1, spot: 1, fallback: 1, storage: 1 };

  multiplier.storage =
    multiplier.storage * (storageCostGranularity === PriceType.HOURLY ? 24 : 1);

  const reducerFn = makeCumulativeReducerFn(multiplier);
  return reducerFn(data, datapoint, index);
};

export const processCumulativeCostChartData = (
  params: ProcessCumulativeCostChartDataParams
): ProcessCumulativeCostChartDataResult => {
  const {
    current,
    projectEndOfDayCost,
    applyUptime,
    storageCostGranularity,
    anomalies,
    chartType,
  } = params;

  const data = current.chartData.reduce(
    projectEndOfDayCost
      ? reduceSelectedRateCost(chartType)
      : (data: ComputeSpendChartData[], datapoint, index) =>
          reduceUptimeCost({
            data,
            datapoint,
            index,
            applyUptime,
            storageCostGranularity,
          }),
    [] as ComputeSpendChartData[]
  );

  anomalies.forEach((anomaly) => {
    const pointToMark = findNearestDataPointToTimestamp(
      data,
      dayjs(anomaly.timestamp)
    );
    if (pointToMark) {
      pointToMark.anomalies.push(anomaly);
    }
  });

  return {
    isLoading: current.isLoading,
    data,
  };
};
